import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { RxIntuitiveTeethHelperService } from '../rx-intuitive-teeth-helper.service';

/**
 * Owns all read-only / permission state for the tooth editor.
 *
 * Both {@link RxIntuitiveToothEditorFacade} and
 * {@link RxIntuitiveToothEditorBridgeFacade} compose this service to expose
 * the same permission observables without sharing a base class.
 */
@Injectable()
export class RxIntuitiveToothEditorReadOnlyService {
	/**
	 * Emits `true` when the tooth editor should be rendered in read-only mode
	 * (shell read-only gate OR the host is an iTero Lab device).
	 */
	readonly toothEditorReadOnly$: Observable<boolean> = combineLatest([
		this.shellQuery.getReadOnlyState('toothEditor'),
		this.hostPlatformService.isIteroLab$
	]).pipe(map(([isReadOnly, isIteroLab]) => isReadOnly || isIteroLab));

	/**
	 * Inverse of {@link toothEditorReadOnly$}: `true` when the user may make
	 * edits.
	 */
	readonly canEditToothEditor$: Observable<boolean> = combineLatest([
		this.shellQuery.getReadOnlyState('toothEditor'),
		this.hostPlatformService.isIteroLab$
	]).pipe(map(([isReadOnly, isIteroLab]) => !isReadOnly && !isIteroLab));

	/**
	 * Whether the "validate for send" flag is active.
	 * Delegates to {@link RxIntuitiveTeethHelperService} for the standard
	 * (non-bridge) flow.  The bridge facade overrides this getter directly.
	 */
	get shouldValidateForSend(): boolean {
		return this.teethHelper.shouldValidateForSend;
	}

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly hostPlatformService: HostPlatformService,
		private readonly teethHelper: RxIntuitiveTeethHelperService
	) {}
}
